import { useState } from "react";
import toast from "react-hot-toast";
import { FiUploadCloud, FiX } from "react-icons/fi";
import { TbLoader2, TbCurrencyRupee } from "react-icons/tb";
import axios from "../../../Utils/axiosInstance";
import { s3Upload } from "../../../Utils/s3Upload";

// Shown ONCE above the sample quotations list — lets the buyer submit a
// single bank transfer proof that gets recorded against every quotation
// in `quotations` (each paid via Direct_Bank_Transfer and awaiting proof).
const SampleBankTransferUploadBanner = ({ quotations, order, onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    bankName: "",
    accountHolderName: "",
    accountNumber: "",
    transferAmount: "",
    paymentMethod: "",
    transactionReference: "",
    transferDate: "",
    transferTime: "",
    buyerRemarks: "",
  });

  if (!quotations || quotations.length === 0) return null;

  const handleFieldChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 25 * 1024 * 1024) {
      toast.error("File size must be less than 25 MB.");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
      "image/gif",
      "image/bmp",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const fileExt = selected.name.split(".").pop().toLowerCase();
    const allowedExts = [
      "pdf", "jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "doc", "docx", "xls", "xlsx"
    ];

    if (!allowedTypes.includes(selected.type) && !allowedExts.includes(fileExt)) {
      toast.error("Invalid file format. Please upload a valid document or image.");
      return;
    }
    setUploading(true);
    try {
      const { url } = await s3Upload(selected, "bank-transfers");
      setFile({
        url,
        name: selected.name,
        type: fileExt || "pdf",
        sizeKb: Math.round(selected.size / 1024),
      });
      toast.success("File ready. Fill in bank details and submit.");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please attach your payment proof file.");
      return;
    }
    const required = [
      "bankName",
      "accountHolderName",
      "accountNumber",
      "transferAmount",
      "paymentMethod",
      "transactionReference",
      "transferDate",
    ];
    const missing = required.filter((k) => !form[k]);
    if (missing.length > 0) {
      toast.error("Please fill in all required bank details.");
      return;
    }

    setUploading(true);
    try {
      let succeeded = 0;
      let failed = 0;

      for (const q of quotations) {
        try {
          await axios.post("/bank-transfer/upload", {
            orderId: order?._id,
            selectedQuotationId: q._id,
            type: "Sample",
            file: {
              ...file,
              bankDetails: form,
            },
          });
          succeeded += 1;
        } catch (err) {
          failed += 1;
        }
      }

      if (succeeded > 0) {
        toast.success(
          failed > 0
            ? `Proof submitted for ${succeeded} quotation(s), ${failed} failed.`
            : "Payment proof submitted for all quotations! 🎉"
        );
        setOpen(false);
        setFile(null);
        setForm({
          bankName: "",
          accountHolderName: "",
          accountNumber: "",
          transferAmount: "",
          paymentMethod: "",
          transactionReference: "",
          transferDate: "",
          transferTime: "",
          buyerRemarks: "",
        });
        await onDone?.();
      } else {
        toast.error("Failed to submit payment proof. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  if (!open) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
            <TbCurrencyRupee className="text-orange-600" size={18} />
          </div>
          <div>
            <p className="text-sm font-bold text-orange-800">
              Bank Transfer Proof Needed
            </p>
            <p className="text-xs text-orange-600 mt-0.5">
              {quotations.length} quotation{quotations.length !== 1 ? "s" : ""} paid via bank transfer — upload proof once to cover all of them.
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 transition-colors flex-shrink-0"
        >
          <FiUploadCloud size={14} /> Upload Proof
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-orange-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-900">
          Upload Bank Transfer Proof — covers {quotations.length} quotation{quotations.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={() => setOpen(false)}
          disabled={uploading}
          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition disabled:opacity-50"
        >
          <FiX size={14} className="text-gray-600" />
        </button>
      </div>

      <label className="block">
        <span className="text-xs font-semibold text-gray-600 mb-1 block">
          Payment Proof (PDF, JPG, PNG, WEBP, DOC, DOCX - max 25 MB)
        </span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.doc,.docx,.xls,.xlsx"
          disabled={uploading}
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 transition"
        />
      </label>
      {file && (
        <p className="text-xs text-green-600 font-semibold">✓ {file.name} ready</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <input
          type="text" placeholder="Bank Name *"
          value={form.bankName}
          onChange={(e) => handleFieldChange("bankName", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="text" placeholder="Account Holder Name *"
          value={form.accountHolderName}
          onChange={(e) => handleFieldChange("accountHolderName", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="text" placeholder="Account Number *"
          value={form.accountNumber}
          onChange={(e) => handleFieldChange("accountNumber", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="number" placeholder="Transfer Amount (₹) *"
          value={form.transferAmount}
          onChange={(e) => handleFieldChange("transferAmount", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={form.paymentMethod}
          onChange={(e) => handleFieldChange("paymentMethod", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Payment Method *</option>
          <option value="IMPS">IMPS</option>
          <option value="NEFT">NEFT</option>
          <option value="RTGS">RTGS</option>
          <option value="UPI">UPI</option>
          <option value="CHEQUE">CHEQUE</option>
        </select>
        <input
          type="text" placeholder="Transaction Reference / UTR *"
          value={form.transactionReference}
          onChange={(e) => handleFieldChange("transactionReference", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={form.transferDate}
          onChange={(e) => handleFieldChange("transferDate", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="time"
          value={form.transferTime}
          onChange={(e) => handleFieldChange("transferTime", e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <textarea
        placeholder="Remarks (optional)"
        value={form.buyerRemarks}
        onChange={(e) => handleFieldChange("buyerRemarks", e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
        rows={2}
      />

      <button
        onClick={handleSubmit}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-sm disabled:opacity-50 transition"
      >
        {uploading ? (
          <><TbLoader2 className="animate-spin" size={16} /> Submitting...</>
        ) : (
          <>Submit Proof for {quotations.length} Quotation{quotations.length !== 1 ? "s" : ""}</>
        )}
      </button>
    </div>
  );
};

export default SampleBankTransferUploadBanner;